/**
 * Constants the engine shares with Foundry's pf2e system (ADR-0001): attribute, save and skill
 * vocabularies, proficiency math, modifier types, and the catalogue of Rule Element keys.
 */

export const ATTRIBUTES = ["str", "dex", "con", "int", "wis", "cha"] as const;
export type Attribute = (typeof ATTRIBUTES)[number];

export const ATTRIBUTE_LABELS: Record<Attribute, string> = {
  str: "Strength",
  dex: "Dexterity",
  con: "Constitution",
  int: "Intelligence",
  wis: "Wisdom",
  cha: "Charisma",
};

export function isAttribute(value: unknown): value is Attribute {
  return typeof value === "string" && (ATTRIBUTES as readonly string[]).includes(value);
}

export const SAVES = ["fortitude", "reflex", "will"] as const;
export type SaveSlug = (typeof SAVES)[number];
export const SAVE_ATTRIBUTES: Record<SaveSlug, Attribute> = { fortitude: "con", reflex: "dex", will: "wis" };
export const SAVE_LABELS: Record<SaveSlug, string> = { fortitude: "Fortitude", reflex: "Reflex", will: "Will" };

export const SKILLS = {
  acrobatics: { label: "Acrobatics", attribute: "dex" },
  arcana: { label: "Arcana", attribute: "int" },
  athletics: { label: "Athletics", attribute: "str" },
  crafting: { label: "Crafting", attribute: "int" },
  deception: { label: "Deception", attribute: "cha" },
  diplomacy: { label: "Diplomacy", attribute: "cha" },
  intimidation: { label: "Intimidation", attribute: "cha" },
  medicine: { label: "Medicine", attribute: "wis" },
  nature: { label: "Nature", attribute: "wis" },
  occultism: { label: "Occultism", attribute: "int" },
  performance: { label: "Performance", attribute: "cha" },
  religion: { label: "Religion", attribute: "wis" },
  society: { label: "Society", attribute: "int" },
  stealth: { label: "Stealth", attribute: "dex" },
  survival: { label: "Survival", attribute: "wis" },
  thievery: { label: "Thievery", attribute: "dex" },
} as const satisfies Record<string, { label: string; attribute: Attribute }>;
export type SkillSlug = keyof typeof SKILLS;
export const SKILL_SLUGS = Object.keys(SKILLS) as SkillSlug[];

export function isSkillSlug(value: unknown): value is SkillSlug {
  return typeof value === "string" && Object.hasOwn(SKILLS, value);
}

export const WEAPON_CATEGORIES = ["unarmed", "simple", "martial", "advanced"] as const;
export type WeaponCategory = (typeof WEAPON_CATEGORIES)[number];
export const ARMOR_CATEGORIES = ["unarmored", "light", "medium", "heavy", "light-barding", "heavy-barding"] as const;
export type ArmorCategory = (typeof ARMOR_CATEGORIES)[number];

/** English labels of the proficiency categories (Foundry's `PF2E.WeaponCategory*` and `PF2E.ArmorCategory*`). */
export const WEAPON_CATEGORY_LABELS: Record<WeaponCategory, string> = {
  unarmed: "Unarmed Attacks",
  simple: "Simple Weapons",
  martial: "Martial Weapons",
  advanced: "Advanced Weapons",
};
export const ARMOR_CATEGORY_LABELS: Record<ArmorCategory, string> = {
  unarmored: "Unarmored Defense",
  light: "Light Armor",
  medium: "Medium Armor",
  heavy: "Heavy Armor",
  "light-barding": "Light Barding",
  "heavy-barding": "Heavy Barding",
};

/** Weapons interchangeable for all rules purposes (Foundry's `CONFIG.PF2E.equivalentWeapons`). */
export const EQUIVALENT_WEAPONS: Readonly<Record<string, string>> = {
  "composite-longbow": "longbow",
  "composite-shortbow": "shortbow",
  "big-boom-gun": "hand-cannon",
  "spoon-gun": "hand-cannon",
};

/** The senses Foundry knows (actor/creature/values.ts at the pinned release). */
export const SENSE_TYPES = [
  "bloodsense", "darkvision", "echolocation", "electromagnetic-sense", "greater-darkvision", "infrared-vision", "lifesense",
  "low-light-vision", "magicsense", "motion-sense", "scent", "see-invisibility", "spiritsense", "thoughtsense", "tremorsense",
  "truesight", "wavesense",
] as const;
export type SenseType = (typeof SENSE_TYPES)[number];
export const SENSE_ACUITIES = ["precise", "imprecise", "vague"] as const;
export type SenseAcuity = (typeof SENSE_ACUITIES)[number];
/** Senses whose acuity is fixed by definition. */
export const SENSES_WITH_MANDATORY_ACUITIES: Partial<Record<SenseType, SenseAcuity>> = {
  darkvision: "precise",
  echolocation: "precise",
  "greater-darkvision": "precise",
  "infrared-vision": "precise",
  "low-light-vision": "precise",
  "see-invisibility": "precise",
  truesight: "precise",
};
export const SENSES_WITH_UNLIMITED_RANGE = ["darkvision", "greater-darkvision", "low-light-vision", "see-invisibility"] as const;

export function isSenseType(value: unknown): value is SenseType {
  return typeof value === "string" && (SENSE_TYPES as readonly string[]).includes(value);
}
export function isSenseAcuity(value: unknown): value is SenseAcuity {
  return typeof value === "string" && (SENSE_ACUITIES as readonly string[]).includes(value);
}

/** Movement types, in Foundry's preparation order. */
export const MOVEMENT_TYPES = ["land", "burrow", "climb", "fly", "swim"] as const;
export type MovementType = (typeof MOVEMENT_TYPES)[number];
export const MOVEMENT_TYPE_LABELS: Record<MovementType, string> = { land: "Land", burrow: "Burrow", climb: "Climb", fly: "Fly", swim: "Swim" };

export function isMovementType(value: unknown): value is MovementType {
  return typeof value === "string" && (MOVEMENT_TYPES as readonly string[]).includes(value);
}

/** Size codes as Foundry stores them, with the slugs used in roll options. */
export const SIZES = ["tiny", "sm", "med", "lg", "huge", "grg"] as const;
export const SIZE_SLUGS = ["tiny", "small", "medium", "large", "huge", "gargantuan"] as const;

export type Rank = 0 | 1 | 2 | 3 | 4;
export const RANK_LABELS = ["Untrained", "Trained", "Expert", "Master", "Legendary"] as const;
/** Rank names as Rule Elements spell them (`maxRank: "master"`). */
export const PROFICIENCY_RANKS = ["untrained", "trained", "expert", "master", "legendary"] as const;
export type ProficiencyRankName = (typeof PROFICIENCY_RANKS)[number];
/** Proficiency bonus by rank before the Level is added. */
export const PROFICIENCY_BONUS: readonly [number, number, number, number, number] = [0, 2, 4, 6, 8];
export const PROFICIENCY_RANK_OPTION = [
  "proficiency:untrained",
  "proficiency:trained",
  "proficiency:expert",
  "proficiency:master",
  "proficiency:legendary",
] as const;

export function clampRank(value: unknown): Rank {
  const n = typeof value === "number" ? Math.trunc(value) : Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(4, Math.max(0, n)) as Rank;
}

export const MODIFIER_TYPES = ["ability", "circumstance", "item", "potency", "proficiency", "status", "untyped"] as const;
export type ModifierType = (typeof MODIFIER_TYPES)[number];

export function isModifierType(value: unknown): value is ModifierType {
  return typeof value === "string" && (MODIFIER_TYPES as readonly string[]).includes(value);
}

export const DAMAGE_TYPES = [
  "acid", "cold", "electricity", "fire", "force", "sonic", "vitality", "void",
  "bleed", "bludgeoning", "piercing", "slashing",
  "mental", "poison", "spirit", "untyped",
] as const;
export const DAMAGE_CATEGORIES_UNIQUE = ["persistent", "precision", "splash"] as const;

/** Foundry's PHYSICAL_ITEM_TYPES: the inventory (#11). */
export const PHYSICAL_ITEM_TYPES = ["ammo", "armor", "backpack", "book", "consumable", "equipment", "shield", "treasure", "weapon"] as const;

export const FEATURE_CATEGORIES = ["ancestryfeature", "calling", "classfeature", "curse", "deityboon", "pfsboon"] as const;

/**
 * Every Rule Element key Foundry's system defines (src/module/rules/index.ts at the pinned
 * release). A key outside this list is unknown to Foundry as well; both cases surface as
 * Unsupported until a handler exists.
 */
export const FOUNDRY_RULE_ELEMENT_KEYS = [
  "ActiveEffectLike", "ActorTraits", "AdjustDegreeOfSuccess", "AdjustModifier", "AdjustStrike", "Aura",
  "BaseSpeed", "BattleForm", "ChoiceSet", "CraftingAbility", "CreatureSize", "CriticalSpecialization",
  "DamageAlteration", "DamageDice", "DexterityModifierCap", "EphemeralEffect", "FastHealing", "FlatModifier",
  "GrantItem", "Immunity", "ItemAlteration", "LoseHitPoints", "MartialProficiency", "MultipleAttackPenalty",
  "Note", "Resistance", "RollOption", "RollTwice", "Sense", "SpecialResource", "SpecialStatistic", "Strike",
  "SubstituteRoll", "TempHP", "TokenEffectIcon", "TokenImage", "TokenLight", "TokenMark", "TokenName", "Weakness",
] as const;

/** Kinds that only affect the Foundry canvas: deliberate no-ops (ADR-0001), never Unsupported. */
export const CANVAS_ONLY_KEYS = ["TokenEffectIcon", "TokenImage", "TokenLight", "TokenMark", "TokenName"] as const;

/** Damage die sizes in ascending order, and their face counts. */
export const DAMAGE_DIE_SIZES = ["d4", "d6", "d8", "d10", "d12"] as const;
export type DamageDieSize = (typeof DAMAGE_DIE_SIZES)[number];
export const DAMAGE_DICE_FACES = [4, 6, 8, 10, 12] as const;
export type DamageDiceFaces = (typeof DAMAGE_DICE_FACES)[number];

export function isDamageDieSize(value: unknown): value is DamageDieSize {
  return typeof value === "string" && (DAMAGE_DIE_SIZES as readonly string[]).includes(value);
}
export function isDamageDiceFaces(value: unknown): value is DamageDiceFaces {
  return typeof value === "number" && (DAMAGE_DICE_FACES as readonly number[]).includes(value);
}
export function isDamageType(value: unknown): value is DamageType {
  return typeof value === "string" && (DAMAGE_TYPES as readonly string[]).includes(value);
}
export type DamageType = (typeof DAMAGE_TYPES)[number];
export type DamageCategoryUnique = (typeof DAMAGE_CATEGORIES_UNIQUE)[number];

/** The next die size up or down (Foundry: nextDamageDieSize); unchanged at either end. */
export function nextDieSize(size: DamageDieSize, direction: "upgrade" | "downgrade"): DamageDieSize {
  const index = DAMAGE_DIE_SIZES.indexOf(size) + (direction === "upgrade" ? 1 : -1);
  return DAMAGE_DIE_SIZES[index] ?? size;
}

/** Foundry's BASE_DAMAGE_TYPES_TO_CATEGORIES. */
export const DAMAGE_TYPE_CATEGORIES: Readonly<Record<string, "physical" | "energy" | null>> = {
  bludgeoning: "physical", piercing: "physical", slashing: "physical", bleed: "physical",
  acid: "energy", cold: "energy", electricity: "energy", fire: "energy", sonic: "energy", vitality: "energy", void: "energy", force: "energy",
  mental: null, poison: null, spirit: null, untyped: null,
};

/** Precious materials whose presence changes how damage is resisted (Foundry: MATERIAL_DAMAGE_EFFECTS plus the config list). */
export const MATERIAL_DAMAGE_EFFECTS = [
  "abysium", "adamantine", "cold-iron", "dawnsilver", "djezet", "duskwood", "inubrix", "keep-stone", "noqual", "orichalcum",
  "peachwood", "siccatite", "silver", "sisterstone-dusk", "sisterstone-scarlet", "sovereign-steel", "warpglass",
] as const;

export const DEGREE_OF_SUCCESS_STRINGS = ["criticalFailure", "failure", "success", "criticalSuccess"] as const;
export type DegreeOfSuccessString = (typeof DEGREE_OF_SUCCESS_STRINGS)[number];

/** English size labels (Foundry's `PF2E.ActorSize*`). */
export const SIZE_LABELS: Record<(typeof SIZES)[number], string> = { tiny: "Tiny", sm: "Small", med: "Medium", lg: "Large", huge: "Huge", grg: "Gargantuan" };
/** Reach by size (Foundry: SIZE_TO_REACH). */
export const SIZE_TO_REACH: Record<(typeof SIZES)[number], number> = { tiny: 0, sm: 5, med: 5, lg: 10, huge: 15, grg: 20 };

/** Weapon groups that are always ranged (Foundry: MANDATORY_RANGED_GROUPS). */
export const MANDATORY_RANGED_GROUPS = ["bomb", "bow", "crossbow", "firearm", "sling"] as const;
/** Traits that only make sense on one side of the melee/ranged divide (Foundry: item/weapon/values.ts). */
export const RANGED_ONLY_TRAITS = ["brutal", "cobbled", "combination", "double-barrel", "fatal-aim-d10", "fatal-aim-d12", "kickback", "propulsive", "scatter-5", "scatter-10", "scatter-15", "scatter-20", "splash", "repeating", "thrown"] as const;
export const MELEE_ONLY_TRAITS = ["brace", "disarm", "grapple", "reach", "shove", "trip"] as const;
/** Base weapons that are thrown by nature (Foundry: CONFIG.PF2E.thrownBaseWeapons). */
export const THROWN_BASE_WEAPONS = ["alchemical-bomb", "grenade"] as const;

/** Weapon group labels (Foundry's `PF2E.WeaponGroup*`), used when the Snapshot's table is not at hand. */
export const WEAPON_GROUP_LABELS: Readonly<Record<string, string>> = {
  axe: "Axe", bomb: "Bomb", bow: "Bow", brawling: "Brawling", club: "Club", corrosive: "Corrosive", crossbow: "Crossbow", cryo: "Cryo", dart: "Dart",
  firearm: "Firearm", flail: "Flail", flame: "Flame", grenade: "Grenade", hammer: "Hammer", knife: "Knife", laser: "Laser", mental: "Mental", pick: "Pick",
  plasma: "Plasma", poison: "Poison", polearm: "Polearm", projectile: "Projectile", shield: "Shield", shock: "Shock", sling: "Sling", sniper: "Sniper",
  sonic: "Sonic", spear: "Spear", sword: "Sword",
};
